import React, { useEffect, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";

import Modal from "../common/Modal";
import { createSellerOffer } from "../../utils/api";
import { updateAllOfferValidity, getPriceValidities } from "../../utils/api";


import {
    getMaterialList,
    getMaterialLocationList,
    getPolymerList,
    getPackagingBagList,
    getSellerId
} from "../../utils/api";

const AddOfferModal = ({ open, onClose, onSubmit }) => {

    const [priceValidity, setPriceValidity] = useState(null);
    const [validities, setValidities] = useState([]);

    useEffect(() => {
        if (!open) return;

        setForm(initialForm); // reset first
        loadInitialData();
        loadValidities();

    }, [open]);

    const formatValidity = (time) => {
        const hours = parseInt(time.split(":")[0], 10);
        return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
    };

    const formatPrice = (value) => {
        let num = Number(value);

        if (isNaN(num)) return 0;

        if (num > 9999.95) num = 9999.95;
        if (num < 0) num = 0;

        return Math.round(num * 20) / 20; // multiple of 0.05
    };

    const initialForm = {
        material: null,
        location: null,
        qty: "",
        price: "",
        polymer: null,
        packaging: null
    };
    const [form, setForm] = useState({
        material: null,
        location: null,
        qty: "",
        price: "",
        polymer: null,
        packaging: null
    });

    const [options, setOptions] = useState({
        materials: [],
        locations: [],
        polymers: [],
        packaging: []
    });

    const seller = getSellerId();

    const loadValidities = async () => {
        try {
            const res = await getPriceValidities();
            setValidities(res?.data || []);
        } catch (err) {
            console.error("Failed to load validities", err);
        }
    };

    useEffect(() => {
        if (!open) return;

        loadInitialData();

    }, [open]);

    const loadInitialData = async () => {
        try {
            const [polymerRes, packagingRes] = await Promise.all([
                getPolymerList(),
                getPackagingBagList()
            ]);

            const polymers = polymerRes?.data || [];
            const packaging = packagingRes?.data || [];

            setOptions(prev => ({
                ...prev,
                polymers,
                packaging
            }));

            // ✅ Auto select first values
            setForm(prev => ({
                ...prev,
                polymer: prev.polymer ?? polymers[0]?.id ?? null,
                packaging: prev.packaging ?? packaging[0]?.id ?? null
            }));

        } catch (err) {
            console.error("Load failed", err);
        }
    };

    // 🔍 Searchable material
    const loadMaterials = async (input) => {
        const res = await getMaterialList({
            seller_id: seller,
            search_text: input,
            page_number: 1,
            page_size: 10
        });

        setOptions(prev => ({
            ...prev,
            materials: res?.data || []
        }));
    };

    // 🔍 Searchable location
    const loadLocations = async (input) => {
        const res = await getMaterialLocationList({
            search_text: input,
            page_number: 1,
            page_size: 10
        });

        setOptions(prev => ({
            ...prev,
            locations: res?.data || []
        }));
    };

    const handleSubmit = async () => {
        try {

            if (!form.qty || !form.price) {
                alert("Quantity and Price are required");
                return;
            }

            const payload = {
                seller_id: seller,

                grade_id: form.material?.grade_id,
                process_type_id: form.material?.process_id,
                brand_id: form.material?.brand_id,
                grade_code_id: form.material?.grade_code_id,

                polymer_id: form.polymer,
                packaging_bag_id: form.packaging,

                qty: Number(form.qty),
                original_price: Number(form.price),

                area_id: form.location?.area_id
            };

            console.log("Submitting:", payload);
            console.log("FORM:", form);
            console.log("PAYLOAD:", payload);

            await createSellerOffer(payload);

            if (priceValidity) {
                await updateAllOfferValidity({
                    seller_id: seller,
                    price_validity: priceValidity
                });
            }
            toast.success("Offer created successfully");
            onSubmit(payload); // refresh table
            setForm(initialForm);
            onClose();

        } catch (err) {
            console.error("Create offer failed", err);
            toast.error(err.message || "Failed to create offer");
        }
    };

    const safeLabel = (m) =>
        [m?.process_name, m?.brand_name, m?.code_name]
            .filter(Boolean)
            .join(" ");

    return (
        <Modal open={open} title="Add New Offer" onClose={onClose}>

            <div className="space-y-4 sm:space-y-5 text-sm sm:text-base">
                {/* Row 1 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Material */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Material</label>
                        <Select
                            classNamePrefix="rs"
                            className="text-sm"
                            placeholder="Search material..."
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: "36px",
                                    fontSize: "14px"
                                })
                            }}
                            onInputChange={(value) => {
                                if (typeof value === "string") loadMaterials(value);
                            }}
                            options={(options.materials || []).map(m => ({
                                value: m.id,
                                label:
                                    [
                                        m?.grade_name,
                                        m?.process_name,
                                        m?.brand_name,
                                        m?.polymer_name,
                                        m?.code_name,
                                        m?.grade_code_name
                                    ]
                                        .filter(Boolean)
                                        .join(" ") || "Unnamed"
                            }))}
                            onChange={(val) =>
                                setForm(prev => ({
                                    ...prev,
                                    material: val
                                        ? options.materials.find(m => m.id === val.value)
                                        : null
                                }))
                            }
                        />
                    </div>

                    {/* Location */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Location</label>
                        <Select
                            classNamePrefix="rs"
                            className="text-sm"
                            placeholder="Search location..."
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: "36px",
                                    fontSize: "14px"
                                })
                            }}
                            onInputChange={(value, meta) => {
                                if (meta.action === "input-change") loadLocations(value);
                            }}
                            options={(options.locations || []).map(l => ({
                                value: l.id,
                                label: l?.name + " " + l?.city_name + " " + l?.state_short_code + " " + l?.pincode || "Unknown"
                            }))}
                            filterOption={null}
                            onChange={(val) =>
                                setForm(prev => ({
                                    ...prev,
                                    location: val
                                        ? options.locations.find(l => l.id === val.value)
                                        : null
                                }))
                            }
                        />
                    </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Quantity */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Quantity (MT)
                        </label>
                        <input
                            type="text"
                            placeholder="Enter quantity"
                            value={form.qty}
                            className="w-full border rounded-md sm:rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "") return setForm(p => ({ ...p, qty: "" }));
                                if (!/^\d{0,6}$/.test(val)) return;
                                setForm(p => ({ ...p, qty: val }));
                            }}
                            onBlur={(e) => {
                                const val = e.target.value || "0";
                                setForm(p => ({ ...p, qty: String(Number(val)) }));
                            }}
                        />
                    </div>

                    {/* Price */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Price (₹/kg)
                        </label>
                        <input
                            type="text"
                            placeholder="Enter price"
                            value={form.price}
                            className="w-full border rounded-md sm:rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "") return setForm(p => ({ ...p, price: "" }));
                                if (!/^\d{0,4}(\.\d{0,2})?$/.test(val)) return;

                                if (/^\d{0,4}\.\d{2}$/.test(val)) {
                                    return setForm(p => ({
                                        ...p,
                                        price: formatPrice(val).toFixed(2)
                                    }));
                                }

                                setForm(p => ({ ...p, price: val }));
                            }}
                            onBlur={(e) => {
                                setForm(p => ({
                                    ...p,
                                    price: formatPrice(e.target.value).toFixed(2)
                                }));
                            }}
                        />
                    </div>
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">

                    {/* Price Validity */}
                    <div className="w-full">
                        <label className="block text-xs sm:text-sm font-medium mb-1">
                            Price Validity
                        </label>

                        <select
                            className="
        w-full
        border rounded-md
        px-2 sm:px-3
        py-1.5 sm:py-2
        text-xs sm:text-sm
        bg-white
        focus:ring-1 focus:ring-blue-500
        outline-none
      "
                            value={priceValidity || ""}
                            onChange={(e) => setPriceValidity(e.target.value)}
                        >
                            <option value="">Select</option>
                            {validities.map(v => (
                                <option key={v.id} value={v.price_validity}>
                                    {formatValidity(v.price_validity)}
                                </option>
                            ))}
                        </select>
                    </div>

                </div>

                {/* Polymer */}
                <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        Polymer Quality
                    </label>

                    <div className="flex flex-wrap gap-2">
                        {options.polymers.map(p => {
                            const isActive = form.polymer === p.id;

                            return (
                                <button
                                    key={p.id}
                                    onClick={() =>
                                        setForm(prev => ({ ...prev, polymer: p.id }))
                                    }
                                    className={`
            px-3 py-1.5
            text-xs sm:text-sm
            rounded-full
            border
            transition-all duration-150

            ${isActive
                                            ? "bg-[var(--base-color)] text-white border-blue-600 shadow-sm"
                                            : "bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                                        }
          `}
                                >
                                    {p.name}
                                </button>
                            );
                        })}
                    </div>
                </div>


                {/* Packaging */}
                <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        Packaging
                    </label>

                    <div className="flex flex-wrap gap-2">
                        {options.packaging.map(p => {
                            const isActive = form.packaging === p.id;

                            return (
                                <button
                                    key={p.id}
                                    onClick={() =>
                                        setForm(prev => ({ ...prev, packaging: p.id }))
                                    }
                                    className={`
            px-3 py-1.5
            text-xs sm:text-sm
            rounded-full
            border
            transition-all duration-150

            ${isActive
                                            ? "bg-[var(--base-color)] text-white border-blue-600 shadow-sm"
                                            : "bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                                        }
          `}
                                >
                                    {p.name}
                                </button>
                            );
                        })}
                    </div>
                </div>


                {/* Actions */}
                <div className="flex justify-end gap-2 sm:gap-3 pt-4 mt-2 border-t">

                    <button
                        onClick={onClose}
                        className="
      btn-cancel
    "
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        className="
      btn-primary
    "
                    >
                        Create Offer
                    </button>

                </div>

            </div>

        </Modal>
    );
};

export default AddOfferModal;